<template>
  <div class="dashboard-container">
    <!-- Loader: Top Progress Bar -->
    <div v-if="loading" class="progress-bar" :style="{ width: progress + '%' }"></div>

    <div class="container">
      <!-- Form Title -->
      <div class="mb-2 page-title">
        <i class="bi bi-file-earmark-text"></i>
        {{ isEdit ? "Edit Tagihan Pembayaran" : "Buat Tagihan Pembayaran" }}
      </div>

      <!-- Form -->
      <form @submit.prevent="submitForm" class="admin-form mt-4">
        <div class="row">
          <!-- Tournament Name -->
          <div class="col-lg-6">
            <div class="mb-3">
              <label for="tournament_id" class="form-label">Turnamen</label>
              <select
                class="form-select"
                id="tournament_id"
                name="tournament_id"
                v-model="form.tournament_id"
                :class="{ 'is-invalid': errors.tournament_id }"
              >
                <option value="" disabled>Pilih Turnamen</option>
                <option v-for="tournament in tournaments" :key="tournament.id" :value="tournament.id">
                  {{ tournament.name }}
                </option>
              </select>
              <div class="invalid-feedback">{{ errors.tournament_id }}</div>
            </div>

            <!-- Bank Details -->
            <div class="mb-3">
              <label for="bank_name" class="form-label">Nama Bank</label>
              <input
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.bank_name }"
                id="bank_name"
                name="bank_name"
                v-model="form.bank_name"
              />
              <div class="invalid-feedback">{{ errors.bank_name }}</div>
            </div>

            <!-- Account Number -->
            <div class="mb-3">
              <label for="account_number" class="form-label">Nomor Rekening</label>
              <input
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.account_number }"
                id="account_number"
                name="account_number"
                v-model="form.account_number"
              />
              <div class="invalid-feedback">{{ errors.account_number }}</div>
            </div>

            <!-- Account Name -->
            <div class="mb-3">
              <label for="account_name" class="form-label">Atas Nama Rekening</label>
              <input
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.account_name }"
                id="account_name"
                name="account_name"
                v-model="form.account_name"
              />
              <div class="invalid-feedback">{{ errors.account_name }}</div>
            </div>
            

            <!-- Notes -->
            <div class="mb-3">
              <label for="notes" class="form-label">Catatan</label>
              <textarea
                class="form-control"
                :class="{ 'is-invalid': errors.notes }"
                id="notes"
                v-model="form.notes"
              ></textarea>
              <div class="invalid-feedback">{{ errors.notes }}</div>
            </div>
          </div>
        </div>
        <div v-if="isEdit" class="row mb-3">
          <div class="col-lg-12 d-flex justify-content-end">
            <button 
              type="button" 
              class="button button-primary" 
              :disabled="loading" 
              @click="togglePopup"
            >
              <i class="bi" :class="showPopup ? 'bi bi-x-square' : 'bi bi-plus-square'"></i>
              <span>{{ showPopup ? 'Tutup' : 'Tambah Atlet / Tim' }}</span>
            </button>
          </div>
        </div>
        <div class="row mb-3">
          <div class="col-lg-12">
            <div v-if="showPopup" class="popup-overlay">
              <table class="table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Kontingen</th>
                    <th>Nama Atlet / Tim</th>
                    <th>Kategori</th>
                    <th>Kategori Tanding</th>
                    <th>Kategori Usia</th> 
                    <th>Kelas</th>
                    <th>Biaya Pendaftaran</th>
                    <th class="text-center">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(member, index) in unselectedMembers" :key="member.id">
                    <td>{{ index + 1 }}</td>
                    <td>{{ member.contingent?.name }}</td>
                    <td>{{ member.name }}</td>
                    <td>{{ member.championship_category?.name }}</td>
                    <td>{{ member.match_category?.name }}</td>
                    <td>{{ member.age_category?.name || '-' }}</td>
                    <td>
                      <div v-if="member.category_class">
                        {{ member.category_class?.name }}<br />
                        <small class="text-muted">
                          {{ member.category_class?.weight_min }} - {{ member.category_class?.weight_max }} kg
                        </small>
                      </div>
                      <div v-else>-</div>
                    </td>

                    <td>{{ formatNumber(member.registration_fee) }}</td>
                    <td v-if="isEdit" class="action-column text-center">
                      <button 
                        v-if="!member.exists_in_billing_details"
                        type="button" 
                        class="button button-primary" 
                        :disabled="loading || member.isAdding" 
                        @click="addMemberToBilling(member)"
                      >
                        <i class="bi" :class="member.isAdding ? 'bi bi-hourglass-split' : 'bi bi-plus-square'"></i>
                        <span>{{ member.isAdding ? 'Menambahkan...' : 'Tambah' }}</span>
                      </button>
                      <span v-else class="text-success"><i class="bi bi-check-circle"></i> Ditambahkan</span>
                    </td>

                    
                  </tr>
                  <tr v-if="members.length === 0">
                    <td colspan="9" class="text-center">Tidak ada atlet ditemukan.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>


        <!-- Members Table -->
        <table class="table">
          <thead>
            <tr class="table-header">
              <th colspan="9" class="header">
                <div class="w-100 d-flex gap-2">
                    <!-- Filter Contingent -->
                  <select v-model="selectedContingent" class="form-select w-auto">
                    <option value="">Semua Kontingen</option>
                    <option v-for="c in contingents" :key="c.id" :value="c.id">{{ c.name }}</option>
                  </select>

                  <!-- Filter Age Category -->
                  <select v-model="selectedAgeCategory" @change="fetchCategoryClasses" class="form-select w-auto">
                    <option value="">Semua Kategori Usia</option>
                    <option v-for="age in ageCategories" :key="age.id" :value="age.id">{{ age.name }}</option>
                  </select>


                  <!-- Filter Category Class -->
                  <select v-model="selectedCategoryClass" class="form-select w-auto">
                    <option value="">Semua Kelas</option>
                    <option v-for="cls in categoryClasses" :key="cls.id" :value="cls.id">
                      {{ cls.name }} ({{ cls.weight_min }} - {{ cls.weight_max }} kg)
                    </option>
                  </select>
                </div>
                
              </th>
            </tr>

            <tr>
              <th>#</th>
              <th>Kontingen</th>
              <th>Nama Atlet / Tim</th>
              <th>Kategori</th>
              <th>Kategori Tanding</th>
              <th>Kategori Usia</th> 
              <th>Kelas</th>
              <th>Biaya Pendaftaran</th>
              <th class="text-center">Pilih</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(member, index) in filteredMembers" :key="member.id">

              <td>{{ index + 1 }}</td>
              <td>{{ member.contingent?.name || '-' }}</td>
              <td>{{ member.name }}</td>
              <td>{{ member.championship_category?.name || '-' }}</td>
              <td>{{ member.match_category?.name || '-' }}</td>
              <td>{{ member.age_category?.name || '-' }}</td>
              <td>
                <div v-if="member.category_class">
                  {{ member.category_class?.name }}<br />
                  <small class="text-muted">
                    {{ member.category_class?.weight_min }} - {{ member.category_class?.weight_max }} kg
                  </small>
                </div>
                <div v-else>-</div>
              </td>
              <td>{{ formatNumber(member.registration_fee || member.amount) }}</td>
              <td v-if="isEdit" class="action-column text-center">
                <input v-if="member.exists_in_billing_details"
                  type="checkbox"
                  :id="'member-' + member.id"
                  v-model="selectedMembers"
                  :value="member.id"
                />

                

              </td>
              <td v-else class="action-column text-center">
                <input v-if="!member.exists_in_billing_details"
                  type="checkbox"
                  :id="'member-' + member.id"
                  v-model="selectedMembers"
                  :value="member.id"
                />

                

              </td>
            </tr>
            <tr v-if="members.length === 0">
              <td colspan="9" class="text-center py-4 text-muted">Belum ada atlet atau tim yang terdaftar.</td>
            </tr>
          </tbody>
        </table>

        <!-- Submit Button -->
        <div class="row">
          <div class="col-lg-12 text-center">
            <button type="submit" class="button button-primary" :disabled="loading">
              <i class="bi bi-floppy"></i>
              <span>{{ isEdit ? "Perbarui Data Tagihan" : "Simpan Data Tagihan" }}</span>
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>

  
</template>

<script>
import http from "@/services/http";
import { useToast } from "vue-toastification";

export default {
  name: "PaymentForm",
  props: {
    isEdit: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      tournaments: [],
      members: [],
      unselectedMembers: [],
      selectedMembers: [], // This will hold the selected members' IDs
      selectedContingent: '',
      selectedAgeCategory: '',
      selectedCategoryClass: '',
      contingents: [],
      ageCategories: [],
      categoryClasses: [],
      age_category_id: '',
      form: {
        tournament_id: null,
        bank_name: "",
        account_number: "",
        account_name: "",
        notes: "",
      },
      errors: {},
      loading: false,
      progress: 0,
      paymentId: null, // Define paymentId for update mode
      showPopup: false, // Control the pop-up visibility
    };
  },

  computed: {
    /*filteredMembers() {
      if (!this.selectedContingent) {
        return this.members;
      }

      return this.members.filter(m =>
        m.contingent && m.contingent.id == this.selectedContingent
      );
    }*/

    filteredMembers() {
      return this.members.filter((member) => {
        const cId = member.contingent_id || member.contingent?.id;
        const aId = member.age_category_id || member.age_category?.id;
        const clId = member.category_class_id || member.category_class?.id;

        const matchContingent = this.selectedContingent ? cId == this.selectedContingent : true;
        const matchAgeCategory = this.selectedAgeCategory ? aId == this.selectedAgeCategory : true;
        const matchCategoryClass = this.selectedCategoryClass ? clId == this.selectedCategoryClass : true;
        return matchContingent && matchAgeCategory && matchCategoryClass;
      });
    }
  },

  mounted() {

    this.fetchContingents();
     this.fetchAgeCategories();
    this.fetchCategoryClasses();
  },

  created() {
    this.paymentId = this.$route.params.id;
    this.fetchActiveTournaments();

    this.loadTeamMembers();
    if (this.isEdit && this.paymentId) {
      this.fetchPaymentDetail(this.paymentId);
    }
  },

  beforeRouteUpdate(to, from, next) {
    if (this.isEdit && to.params.id !== this.paymentId) {
      this.paymentId = to.params.id;
      this.fetchPaymentDetail(this.paymentId);
    }
    next();
  },

  watch: {
    selectedAgeCategory() {
      this.selectedCategoryClass = '';
    },
    'form.tournament_id': function (newVal) {
      if (newVal) {
        this.fetchContingents();
      } else {
        this.contingents = [];
        this.selectedContingent = '';
      }
    },
    // Watch for changes to the paymentId from the route params
    '$route.params.id': {
      immediate: true,
      handler(newId) {
        // Fetch contingent data when route param changes
        if (this.isEdit && newId) {
          this.paymentId = newId;
          this.fetchPaymentDetail(newId); 
        }
      },
    }
  },

  methods: {

    /*
     * Kegagalan HARUS ditangkap.
     * Tanpa .catch, permintaan yang gagal berubah jadi unhandled rejection:
     * daftar kategori tetap kosong, tidak ada pesan apa pun, dan pengguna
     * mengira memang belum ada datanya. Yang terjadi sebenarnya jaringan
     * putus atau sesi berakhir.
     */
    async fetchAgeCategories() {
      try {
        const { data } = await http.get('fetch-age-categories');
        this.ageCategories = data || [];
      } catch (error) {
        this.ageCategories = [];
      }
    },

    async fetchCategoryClasses() {
      if (!this.selectedAgeCategory) {
        this.categoryClasses = [];
        this.selectedCategoryClass = '';
        return;
      }

      try {
        const { data } = await http.get(
          `/fetch-category-classes?age_category_id=${this.selectedAgeCategory}`
        );
        this.categoryClasses = data || [];
        this.selectedCategoryClass = '';
      } catch (error) {
        this.categoryClasses = [];
        this.selectedCategoryClass = '';
      }
    },

    async fetchContingents() {
      if (!this.form.tournament_id) {
        this.contingents = [];
        return;
      }

      try {
        const response = await http.get(
          `/contingents/by-tournament/${this.form.tournament_id}`
        );
        this.contingents = response.data || [];
      } catch (error) {
        console.error("Error fetching contingents:", error);
        this.contingents = [];
      }
    },

    togglePopup() {
      this.showPopup = !this.showPopup;
    },

    formatNumber(value) {
      if (value == null) return "-";
      return new Intl.NumberFormat("id-ID", { 
        style: "currency", 
        currency: "IDR", 
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    },

    async fetchActiveTournaments() {
      try {
        const response = await http.get("/tournaments/active");
        this.tournaments = response.data || [];
      } catch (error) {
        console.error("Error fetching tournaments:", error);
      }
    },

    async loadTeamMembers() {
      this.loading = true;
      try {
        const response = await http.get("/team-members?fetch_all=true");
        this.members = response.data || [];
        this.unselectedMembers = response.data || [];
      } catch (error) {
        console.error("Error loading members:", error);
        this.members = [];
        this.unselectedMembers = [];
      } finally {
        this.loading = false;
      }
    },
    
    async fetchPaymentDetail(id) {
      this.loading = true;
      try {
        const response = await http.get(`/billings/${id}`);
        if (response.data) {
          this.form = {
            tournament_id: response.data.tournament_id || null,
            bank_name: response.data.bank_name || "",
            account_number: response.data.account_number || "",
            account_name: response.data.account_name || "",
            members: response.data.billing_details || [],
            total_amount: response.data.amount || response.data.total_amount || 0,
            notes: response.data.notes || "",
          };

          this.members = response.data.billing_details || [];
          this.selectedMembers = this.members
            .filter(member => member.exists_in_billing_details)
            .map(member => member.id);
        }
      } catch (error) {
        this.toast.error("Gagal memuat rincian tagihan.");
        console.error("Error fetching payment details:", error);
      } finally {
        this.loading = false;
      }
    },

    async addMemberToBilling(member) {
      if (!this.paymentId) {
        this.toast.error("ID Pembayaran diperlukan sebelum menambahkan anggota.");
        return;
      }

      member.isAdding = true;

      try {
        const response = await http.post(
          `/billings/add-member`,
          {
            billing_id: this.paymentId,
            team_member_id: member.id,
            tournament_category_id: member.tournament_category_id || member.match_category?.tournament_categories?.[0]?.id,
            amount: member.registration_fee || member.amount,
          }
        );

        if (response.status === 201 || response.status === 200) {
          this.toast.success("Anggota berhasil ditambahkan ke tagihan.");
          member.exists_in_billing_details = true;
          this.selectedMembers.push(member.id);
          this.fetchPaymentDetail(this.paymentId);
        } else {
          this.toast.error(response.data?.message || "Gagal menambahkan anggota.");
        }
      } catch (error) {
        console.error("Error adding member to billing:", error);
        this.toast.error(error.response?.data?.message || "Terjadi kesalahan saat menambahkan anggota.");
      } finally {
        member.isAdding = false;
      }
    },

    async submitForm() {
      this.loading = true;
      try {
        const payload = {
          ...this.form,
          member_ids: this.selectedMembers,
        };

        const method = this.isEdit ? "put" : "post";
        const endpoint = this.isEdit ? `/billings/${this.paymentId}` : "/billings";

        await http[method](endpoint, payload);

        this.toast.success(this.isEdit ? "Tagihan berhasil diperbarui" : "Tagihan berhasil disimpan");
        this.$router.push("/admin/payment");
      } catch (error) {
        this.toast.error(error.response?.data?.message || "Gagal menyimpan data tagihan");
        this.errors = error.response?.data?.errors || {};
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>


<style scoped>

.progress-bar {
  position: absolute;
  top: 0;
  left: 0;
  height: 5px;
  background: var(--grad-flame);
  animation: loader-animation 1.5s infinite;
}

.page-title {
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 20px;
}

.invalid-feedback {
  display: block;
  color: #dc3545;
}

/* Animasi garis loader */
@keyframes loader-animation {
  0% {
    width: 0;
    background: var(--grad-flame); /* Warna awal */
  }
  50% {
    width: 50%;
    background: var(--grad-flame); /* Warna saat animasi */
  }
  100% {
    width: 100%;
    background: var(--grad-flame);
  }
}
</style>
